import * as Sentry from '@sentry/nextjs';

import { LanguageCodeEnum } from '../config/common/constants/lang';
import { COOKIE_CITY, COOKIE_IP, COOKIE_USER_AGENT, COOKIE_LOCALE } from '../config/common/constants/storage';
import { buildQueryString } from '../lib/api/buildQuery';
import { getCookie } from '../lib/api/getCookie';
import { trackApiError } from '../lib/trackError';

// @todo: вынести в файл типов все интерфейсы
interface HttpConfig {
  locale: LanguageCodeEnum;
  ip: string;
  city: string;
  userAgent: string;
}

interface NextRequestSettings {
  next?: {
    tags?: string[];
  };
}

interface RequestOptions extends NextRequestSettings {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';
  headers?: Record<string, string>;
  body?: object;
}

// @todo: поработать над неймингом
interface RequestOptionsProps extends NextRequestSettings {
  headers?: Record<string, string>;
}

const isMockingEnabled = process.env.NEXT_PUBLIC_API_MOCKING === 'enabled';
const isDevelopment = process.env.NODE_ENV === 'development';
let isInitiatedMSW = false;

if (typeof window !== 'undefined' && isDevelopment && isMockingEnabled && !isInitiatedMSW) {
  import('@/shared/lib/msw/init/browser')
    .then(({ worker }) => {
      isInitiatedMSW = true;

      return worker.start({
        waitUntilReady: true,
        onUnhandledRequest: 'bypass',
      });
    })
    .catch((err: Error) => {
      console.error('[MSW] Failed to initialize:', err);
    });
}

class Http {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;
  private config: HttpConfig;
  private trackError: ReturnType<typeof trackApiError>;

  constructor(baseUrl: string, config: HttpConfig) {
    this.baseUrl = baseUrl;
    this.config = config;
    this.defaultHeaders = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };

    this.trackError = trackApiError(this.config);

    if (this.config.city === 'unknown') {
      Sentry.captureMessage('city unknown', {
        level: 'error',
        tags: {
          custom: true,
        },
        extra: {
          config,
        },
      });
    }
  }

  private async request<T>(endpoint: string, options: RequestOptions): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      ...this.defaultHeaders,
      ...options.headers,
      'Accept-Language': this.config.locale,
      'X-User-IP': this.config.ip,
      'X-User-City': this.config.city,
      'User-Agent': this.config.userAgent,
    };

    const config: RequestInit = {
      method: options.method,
      headers,
    };

    if (options.body) {
      config.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const data = await response.json();

        if (data.message) {
          throw new Error(data.message);
        }

        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');

      if (options.method === 'HEAD') {
        return {
          status: response.status,
          ok: response.ok,
        } as unknown as T;
      } else if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return (await response.text()) as unknown as T;
      }
    } catch (error) {
      this.trackError({
        status: 0,
        endpoint: endpoint,
        method: options.method,
        message: `Request Error: ${error}`,
      });

      throw error;
    }
  }

  public async get<T>(endpoint: string, settings?: RequestOptionsProps, query?: object): Promise<T> {
    const url = query ? `${endpoint}?${buildQueryString(query)}` : endpoint;

    const options: RequestOptions = {
      method: 'GET',
    };

    if (settings) {
      if (settings.next) {
        options.next = settings.next;
      }

      if (settings.headers) {
        options.headers = settings.headers;
      }
    }

    return this.request<T>(url, options);
  }

  public async post<T>(endpoint: string, body: object): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body });
  }

  public async put<T>(endpoint: string, body: object): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  public async patch<T>(endpoint: string, body: object): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', body });
  }

  public async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  public async head<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'HEAD' });
  }
}

export const createHttp = (config: HttpConfig) => new Http(process.env.NEXT_PUBLIC_API_BASE_URL as string, config);

export const getHttpPayload = (headersList: { cookie: string; setCookie: string }): HttpConfig => {
  const cookie = headersList.cookie || '';
  const setCookieHeader = headersList.setCookie || '';

  const useSetCookies =
    setCookieHeader.includes(COOKIE_IP) &&
    setCookieHeader.includes(COOKIE_CITY) &&
    setCookieHeader.includes(COOKIE_USER_AGENT);

  const source = useSetCookies ? setCookieHeader : cookie;
  const typeCookie: 'cookie' | 'set-cookie' = useSetCookies ? 'set-cookie' : 'cookie';

  return {
    locale:
      ((getCookie(COOKIE_LOCALE, headersList.cookie, 'cookie') ||
        getCookie(COOKIE_LOCALE, headersList.setCookie, 'set-cookie')) as LanguageCodeEnum) || LanguageCodeEnum.RU,
    ip: getCookie(COOKIE_IP, source, typeCookie) || '127.0.0.1',
    city: getCookie(COOKIE_CITY, source, typeCookie) || '',
    userAgent: getCookie(COOKIE_USER_AGENT, source, typeCookie) || '',
  };
};

export const getCachedHttp = (headersList: { cookie: string; setCookie: string }) => {
  const payload = getHttpPayload(headersList);

  return createHttp(payload);
};

export type { HttpConfig };
export type HttpClient = ReturnType<typeof createHttp>;
